import { useEffect, useState } from 'react';
import type { Capabilities, Conversation } from '../shared/types';
import { get, post } from './api';
import { ChatView } from './components/ChatView';
import { Settings } from './components/Settings';
import { Sidebar } from './components/Sidebar';
import { IconSparkles } from './components/icons';
import { useSocket } from './hooks/useSocket';
import { rememberRoomOwner } from './roomOwners';
import { useChat } from './state';

function QuickDialog({ mode, onClose }: { mode: 'room' | 'join'; onClose: () => void }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const upsertConversation = useChat((state) => state.upsertConversation);
  const setActive = useChat((state) => state.setActive);
  const me = useChat((state) => state.me);

  const submit = async () => {
    if (!value.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const path = mode === 'room' ? '/api/mesh/rooms' : '/api/mesh/join';
      const body = mode === 'room' ? { name: value.trim(), authorId: me.id } : { invite: value.trim() };
      const result = await post<{ conversation: Conversation; ownerKey?: string }>(path, body);
      if (result.ownerKey) rememberRoomOwner(result.conversation.id, result.ownerKey);
      upsertConversation(result.conversation);
      setActive(result.conversation.id);
      onClose();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal small" onClick={(event) => event.stopPropagation()}>
        <header className="modal-head">
          <strong>{mode === 'room' ? 'New mesh room' : 'Join mesh room'}</strong>
        </header>
        <div className="modal-body">
          <input
            autoFocus
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void submit();
            }}
            placeholder={mode === 'room' ? 'Room name' : 'Invite code'}
          />
          <p className="hint">
            {mode === 'room'
              ? 'Messages replicate directly between peers over Hyperswarm.'
              : 'Ask the room owner for the invite code shown in its chat header.'}
          </p>
          {error ? <p className="status error">{error}</p> : null}
        </div>
        <footer className="modal-foot">
          <button className="btn" onClick={() => void submit()} disabled={busy || !value.trim()}>
            {mode === 'room' ? 'Create room' : 'Join'}
          </button>
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
        </footer>
      </div>
    </div>
  );
}

function EmptyState() {
  const capabilities = useChat((state) => state.capabilities);
  const toggleSettings = useChat((state) => state.toggleSettings);
  return (
    <section className="empty">
      <div className="empty-art">
        <IconSparkles size={42} />
      </div>
      <span className="empty-kicker">Private messaging hub</span>
      <h1>Chatsi</h1>
      <p>Telegram-style messaging across Telegram, Discord and peer-to-peer mesh, with LiveKit calls and an AI sidekick.</p>
      <div className="feature-chips">
        <span className={capabilities?.telegram.enabled ? 'chip ok' : 'chip'}>Telegram bots</span>
        <span className={capabilities?.discord.enabled ? 'chip ok' : 'chip'}>Discord bots</span>
        <span className={capabilities?.mesh.enabled ? 'chip ok' : 'chip'}>Pear mesh</span>
        <span className={capabilities?.livekit.enabled ? 'chip ok' : 'chip'}>LiveKit calls</span>
        <span className={capabilities?.ai.enabled ? 'chip ok' : 'chip'}>AI replies</span>
      </div>
      <button className="btn" onClick={() => toggleSettings(true)}>
        Connect an account
      </button>
      <p className="empty-hint">Pick a chat on the left, create a mesh room, or connect a bot in Settings.</p>
    </section>
  );
}

export default function App() {
  useSocket();
  const conversations = useChat((state) => state.conversations);
  const activeId = useChat((state) => state.activeId);
  const settingsOpen = useChat((state) => state.settingsOpen);
  const mobileChat = useChat((state) => state.mobileChat);
  const setConversations = useChat((state) => state.setConversations);
  const setCapabilities = useChat((state) => state.setCapabilities);
  const notify = useChat((state) => state.notify);
  const toast = useChat((state) => state.toast);
  const [dialog, setDialog] = useState<'room' | 'join' | null>(null);

  useEffect(() => {
    Promise.all([
      get<{ conversations: Conversation[] }>('/api/conversations'),
      get<Capabilities>('/api/capabilities'),
    ])
      .then(([list, capabilities]) => {
        setConversations(list.conversations);
        setCapabilities(capabilities);
      })
      .catch((error: Error) => notify(`Cannot reach the server: ${error.message}`, 'error'));
  }, [setConversations, setCapabilities, notify]);

  useEffect(() => {
    if (!activeId && conversations.length) {
      useChat.setState({ activeId: conversations[0].id });
    }
  }, [activeId, conversations]);

  const active = conversations.find((item) => item.id === activeId) ?? null;

  return (
    <div className={`app ${active ? 'chat-open' : ''} ${mobileChat ? 'mobile-chat' : ''}`}>
      <Sidebar onNewRoom={() => setDialog('room')} onJoin={() => setDialog('join')} />
      {active ? <ChatView key={active.id} conversation={active} /> : <EmptyState />}
      {settingsOpen ? <Settings /> : null}
      {dialog ? <QuickDialog mode={dialog} onClose={() => setDialog(null)} /> : null}
      {toast ? <div className={`toast ${toast.kind}`}>{toast.text}</div> : null}
    </div>
  );
}
